<template>
  <main class="object1-page">
    <div style="font-size: 16px; font-weight: bold; margin:5px">
      {{ $t('object1Page.object1') }}
    </div>
    <div v-if="object1"
         class="container"
    >
      <ItemGrid :normal-columns="4"
                :normal-rows="-1"
                :xs-columns="1"
                :xs-rows="-1"
      >
        <MainInput :text="object1.Name"
                   type="text"
                   name="Object1Name"
                   :placeholder="$t('object1Page.name')"
                   :is-readonly="true"
        />

        <MainInput :text="object1.Customer.Name"
                   type="text"
                   name="Customer"
                   :placeholder="$t('object1Page.customer')"
                   :is-readonly="true"
        />
      </ItemGrid>

      <div class="content-section">
        <table class="styled-table">
          <thead>
            <tr>
              <th @click="toggleDocumentTable">
                {{ isDocumentTableVisible ? '▼' : '▲' }} {{ $t('taskPage.documents') }}
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <thead>
            <tr>
              <th
                @click="setSort({ key: 'Name', reverse: false })"
              >
                {{ $t('taskPage.documentName') }}
              </th>
              <th>
                {{ $t('taskPage.documentFileExtension') }}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-if="!isReadOnly" v-show="isDocumentTableVisible" class="attachment-row">
              <td colspan="3">
                <FileDropZone :title="isUploadingDocument ? $t('documentPage.uploading') : $t('taskPage.attachDocument')"
                              :hint="$t('documentPage.dropFilesHere')"
                              :drop-label="$t('documentPage.dropFilesNow')"
                              :choose-label="$t('documentPage.chooseFiles')"
                              :accepted-types="acceptedFileTypesLabel"
                              :accept="acceptedFileExtensions"
                              :disabled="isUploadingDocument"
                              @files-selected="attachDocuments"
                />
              </td>
            </tr>
            <tr v-for="document in documents"
                v-show="isDocumentTableVisible"
                :key="document.Id"
                class="taskRow"
            >
              <td class="tdTask">
                {{ document.Name }}
              </td>
              <td class="tdTask">
                {{ document.Extension }}
              </td>
              <td style="cursor: pointer; text-align: right; padding-right:10px;"
                  @click="downloadFile(document)"
              >
                <button v-if="canRemoveDocument(store, document)"
                        class="remove-button"
                        :title="$t('documentPage.remove')"
                        :aria-label="$t('documentPage.remove')"
                        @click.stop="removeDocument(document)"
                >
                  <span class="material-icons">delete</span>
                </button>
                <span class="material-icons">download</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="isUkEnvironment" class="content-section">
        <table class="styled-table">
          <thead>
            <tr>
              <th @click="toggleReportTable">
                {{ isReportTableVisible ? '▼' : '▲' }} {{ $t('taskPage.reports') }}
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <thead>
            <tr>
              <th>{{ $t('taskPage.documentName') }}</th>
              <th>{{ $t('taskPage.documentFileExtension') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-if="!isReadOnly" v-show="isReportTableVisible" class="attachment-row">
              <td colspan="3">
                <FileDropZone :title="isUploadingReport ? $t('documentPage.uploading') : $t('taskPage.attachReport')"
                              :hint="$t('documentPage.dropFilesHere')"
                              :drop-label="$t('documentPage.dropFilesNow')"
                              :choose-label="$t('documentPage.chooseFiles')"
                              :accepted-types="acceptedFileTypesLabel"
                              :accept="acceptedFileExtensions"
                              :disabled="isUploadingReport"
                              @files-selected="attachReports"
                />
              </td>
            </tr>
            <tr v-for="report in reports"
                v-show="isReportTableVisible"
                :key="report.Id"
                class="taskRow"
            >
              <td class="tdTask">{{ report.Name }}</td>
              <td class="tdTask">{{ report.Extension }}</td>
              <td style="cursor: pointer; text-align: right; padding-right:10px;"
                  @click="downloadFile(report)"
              >
                <button v-if="canRemoveDocument(store, report)"
                        class="remove-button"
                        :title="$t('documentPage.remove')"
                        :aria-label="$t('documentPage.remove')"
                        @click.stop="removeDocument(report)"
                >
                  <span class="material-icons">delete</span>
                </button>
                <span class="material-icons">download</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="content-section data-section">
        <TaskList :tasks="tasks"
                  show-header="true"
                  :header-text="$t('object1Page.tasks')"
                  :table-columns="['Nr', 'Priority', 'TaskDescription', 'Status', 'Owner', 'Performers', 'PlannedDate', 'Object1']"
        />
      </div>
      <div class="content-section data-section">
        <TaskList v-if="object1.Level === 1 || object1.Level === 0"
                  :tasks="object1.Children"
                  show-header="true"
                  :header-text="object1.Level === 0 ? $t('object1Page.object2s') : $t('object1Page.object3s')"
                  object-type="objects"
                  :table-columns="['Name', 'Customer']"
        />
      </div>
    </div>
  </main>
</template>
<script setup>
import TaskList from '@/components/task/TaskList.vue'
import ItemGrid from '@/components/grid/ItemGrid.vue'
import MainInput from '@/components/inputs/MainInput.vue'
import FileDropZone from '@/components/inputs/FileDropZone.vue'
import { defineProps, onMounted, computed, watch, ref } from 'vue'
import { useStore } from 'vuex'
import { PostDocument } from '@/api/document.js'
import { useToast } from 'vue-toastification'
import i18n from '@/locales/i18n'
import { isReportEnvironment } from '@/utils/environment.js'
import { canRemoveDocument, confirmAndRemoveDocument } from '@/utils/removeDocument.js'
const store = useStore()

const object1 = computed(() => store.state.object1.currentObject1)
const tasks = computed(() => store.state.object1.tasks)
const reportCategory = 'REPORT'
const isUkEnvironment = isReportEnvironment()
const isReadOnly = computed(() => store.getters['account/isReadOnly'])
const documents = computed(() => (object1.value?.Documents ?? []).filter(document => document.Category !== reportCategory))
const reports = computed(() => (object1.value?.Documents ?? []).filter(document => document.Category === reportCategory))
const toast = useToast()
const props = defineProps({
  id: {
    type: Number,
    required: true
  }
})

const allowedExtensions = computed(() => store.state.document.allowedExtensions)
const acceptedFileExtensions = computed(() => allowedExtensions.value.map(extension => `.${extension}`).join(','))
const acceptedFileTypesLabel = computed(() => allowedExtensions.value.length > 0
  ? i18n.global.t('documentPage.allowedFileTypes', { types: allowedExtensions.value.join(', ') })
  : '')
const isUploadingDocument = ref(false)
const isUploadingReport = ref(false)

onMounted(async () => {
  store.dispatch('document/getAllowedFileExtensions')
  store.dispatch('object1/getObjectById', props.id)
  store.dispatch('object1/getTasksByObject1Id', props.id)
})

async function attachDocuments (files) {
  isUploadingDocument.value = true
  try {
    await uploadFiles(files)
  } finally {
    isUploadingDocument.value = false
  }
}

async function attachReports (files) {
  isUploadingReport.value = true
  try {
    await uploadFiles(files, reportCategory)
  } finally {
    isUploadingReport.value = false
  }
}

async function uploadFiles (files, category = null) {
  const formdata = new FormData()

  const normalizedAllowedExtensions = allowedExtensions.value.map(extension => extension.toLowerCase())
  let allFilesAllowed = true
  for (const file of files) {
    const fileParts = file.name.split('.')
    const fileExtension = fileParts[fileParts.length - 1].toLowerCase()
    if (normalizedAllowedExtensions.indexOf(fileExtension) === -1) {
      allFilesAllowed = false
      break
    }
  }

  if (!allFilesAllowed) {
    toast.info(i18n.global.t('documentPage.fileTypesNotAllowed') + allowedExtensions.value.join(', '))
    return
  }

  for (const file of files) {
    formdata.append('file', file)
  }
  try {
    const postResponse = await PostDocument('object1', object1.value.Id, formdata, category)
    // console.log(postResponse)
    if (postResponse.status === 200) {
      store.dispatch('object1/getObjectById', props.id)
      toast.success(i18n.global.t('documentPage.uploadSuccess'))
    } else {
      toast.warning(i18n.global.t('documentPage.uploadFail'))
    }
  } catch (e) {
    toast.warning(e.message)
  }
}

const isDocumentTableVisible = ref(true)
const toggleDocumentTable = () => {
  isDocumentTableVisible.value = !isDocumentTableVisible.value
}

const isReportTableVisible = ref(true)
const toggleReportTable = () => {
  isReportTableVisible.value = !isReportTableVisible.value
}

async function removeDocument (documentInfo) {
  if (await confirmAndRemoveDocument(documentInfo, toast)) {
    store.dispatch('object1/getObjectById', props.id)
  }
}

async function downloadFile (documentInfo) {
  try {
    const byteCharacters = window.atob(documentInfo.Base64Data)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512)
      const byteNumbers = new Array(slice.length)

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: 'application/' + documentInfo.Extension })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = documentInfo.Name + '.' + documentInfo.Extension
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  } catch (e) {
    console.error('Error during file download:', e)
    toast.error(i18n.global.t('documentPage.DownloadFailed') + '\r\n\r\n' + e.message)
  }
}

onMounted(() => {
  store.dispatch('object1/getObjectById', props.id)
  store.dispatch('object1/getTasksByObject1Id', props.id)
})
watch(() => props.id, (newId, oldId) => {
  store.dispatch('object1/getObjectById', newId)
  store.dispatch('object1/getTasksByObject1Id', newId)
})
</script>

<style lang="scss" scoped>
.object1-page {
  height: 100%;
  overflow: scroll;
  background: var(--mainBackground);
  .container{
    padding: 1rem;
  }
}

.styled-table {
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0;
  font-size: 0.9em;
  font-family: 'Lexend', sans-serif;
  width: 100%;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.content-section {
  margin-top: 24px;
}

.data-section :deep(.styled-table) {
  margin: 0;
}

.styled-table thead tr {
  background-color: var(--mainBlue);
  color: white;
  text-align: left;
  cursor: pointer;
}

.styled-table th {
  padding: 12px 15px;
  text-align: left;
}

.styled-table tbody tr {
  border-bottom: 1px solid lightgrey;
  background-color: white;
  color: black;
}

.remove-button {
  margin-right: 12px;
  padding: 0;
  line-height: 0;
  color: #b3261e;
  vertical-align: middle;
}

.attachment-row td {
  padding: 12px;
  background: white;
}

.styled-table tbody tr:nth-of-type(4n-1),
.styled-table tbody tr:nth-of-type(4n) {
  background-color: var(--alternatingListColor);
  color: black;
}
.taskRow{
  border-bottom: 1px solid lightgrey;
  background-color: white;
  color: black;
  height: 20px;

  .header{
    font-weight: bold;
  }
}

.taskRow:nth-of-type(2n) {
  background-color: var(--alternatingListColor);
  color: black;
}

.taskRow:last-of-type {
  border-bottom: 3px solid var(--mainBlue);
}

.tdTask {
  padding: 10px 15px;
  text-align: left;
  transition: padding 0.4s ease;

}
.textarea-container {
  position: relative;
}

.textarea-container textarea {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  resize: none;
}

.textarea-container button {
  position: absolute;
  right: 0;
  bottom: 10px;
  right: 10px;
  padding: 12px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.textarea-container button:hover {
  background-color: #45a049;
}
</style>
